import { ClockCircleOutlined } from '@ant-design/icons';
import { Button, Card, Col, Modal, Row, Typography } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { errorWithUserMessage } from '../../helpers/NotificationHelper';
import { eventSubmissionQueue } from '../../helpers/eventSubmissionQueue';
import authService from '../../components/api-authorization/AuthorizeService';
import { ApiException, MatchesClient, ValidationWindowVm } from '../../utils/api';

const END_MESSAGE = 'Thanks, your game is submitted. We will get back to you via email as soon as we review the results.';

interface Props {
    matchId: number;
    children: React.ReactNode;
}

/** Both desktop editing routes share the same server-owned attempt. */
export default function ValidationWindow({ matchId, children }: Props): React.ReactElement {
    return authService.validationGamesEnabled
        ? <EnabledValidationWindow matchId={matchId}>{children}</EnabledValidationWindow>
        : <>{children}</>;
}

function EnabledValidationWindow({ matchId, children }: Props): React.ReactElement {
    const [attempt, setAttempt] = useState<ValidationWindowVm | null>(null);
    const [finished, setFinished] = useState(false);
    const [countdownDeadline, setCountdownDeadline] = useState<number>();
    const [saving, setSaving] = useState(false);
    const mounted = useRef(false);
    const submitting = useRef(false);
    const confirmation = useRef<ReturnType<typeof Modal.confirm>>();
    useEffect(() => {
        if (finished) confirmation.current?.destroy();
    }, [finished]);

    useEffect(() => {
        mounted.current = true;
        let cancelled = false;
        let refreshing = false;
        let watch = true;
        let endTimer: ReturnType<typeof setTimeout> | undefined;
        const refresh = async (): Promise<void> => {
            if (refreshing || !watch) return;
            refreshing = true;
            const requestStarted = performance.now();
            try {
                const result = await new MatchesClient().openValidationWindow(matchId);
                if (cancelled) return;
                setAttempt(result);
                if (endTimer) clearTimeout(endTimer);
                if (!result) { watch = false; return; }
                const receivedAt = performance.now();
                const remaining = Date.parse(result.deadline || '') - Date.parse(result.serverUtc)
                    - Math.max(0, receivedAt - requestStarted);
                if (result.submittedAt || remaining <= 0) {
                    setFinished(true);
                    watch = false;
                } else if (Number.isFinite(remaining)) {
                    setCountdownDeadline(receivedAt + remaining);
                    endTimer = setTimeout(() => {
                        setFinished(true);
                        void refresh(); // Persist timeout when reachable; hiding does not need the network.
                    }, remaining);
                }
            } catch (error) {
                if (cancelled) return;
                if (error instanceof ApiException && error.status === 410) {
                    setFinished(true);
                    watch = false;
                    if (endTimer) clearTimeout(endTimer);
                }
                // Keep the editor available and preserve any known deadline.
                // Background polling retries silently when Arena is reachable.
            } finally {
                refreshing = false;
            }
        };
        void refresh();
        const poll = setInterval(() => { void refresh(); }, 15000);
        const focus = (): void => { void refresh(); };
        window.addEventListener('focus', focus);
        const unsubscribe = eventSubmissionQueue.subscribe(event => {
            if (event.type === 'validationSubmitted' && event.matchId === matchId) {
                setFinished(true);
                watch = false;
            }
        });
        return () => {
            cancelled = true;
            confirmation.current?.destroy();
            mounted.current = false;
            clearInterval(poll);
            if (endTimer) clearTimeout(endTimer);
            window.removeEventListener('focus', focus);
            unsubscribe();
        };
    }, [matchId]);

    const submit = async (): Promise<void> => {
        if (submitting.current || !mounted.current) return;
        submitting.current = true;
        setSaving(true);
        try {
            const result = await new MatchesClient().submitValidationWindow(matchId);
            if (mounted.current && result?.submittedAt) setFinished(true);
        } catch (error) {
            if (mounted.current) errorWithUserMessage(error);
        } finally {
            submitting.current = false;
            if (mounted.current) setSaving(false);
        }
    };

    if (finished) return <p>{END_MESSAGE}</p>;
    return <>
        {attempt && !attempt.isPractice && <Card size="small" style={{ marginBottom: 16, borderRadius: 8 }}>
            <Row align="middle" gutter={[24, 16]}>
                {countdownDeadline !== undefined && <Col flex="none">
                    <ValidationCountdown deadline={countdownDeadline} />
                </Col>}
                <Col flex="1" style={{ minWidth: 240 }}>
                    <Typography.Paragraph type="secondary" style={{ marginBottom: 0, maxWidth: 720 }}>
                        When you mark the match as finished in the bottom right and you are sure you are finished with your game, submit the game.
                    </Typography.Paragraph>
                </Col>
                <Col flex="none">
                    <Button type="primary" size="large" loading={saving} onClick={(): void => {
                        confirmation.current = Modal.confirm({ title: 'Are you sure?', onOk: submit });
                    }}>Submit</Button>
                </Col>
            </Row>
        </Card>}
        {children}
    </>;
}

function ValidationCountdown({ deadline }: { deadline: number }): React.ReactElement {
    const [now, setNow] = useState(() => performance.now());
    useEffect(() => {
        setNow(performance.now());
        const tick = setInterval(() => setNow(performance.now()), 1000);
        return () => clearInterval(tick);
    }, [deadline]);
    const seconds = Math.max(0, Math.ceil((deadline - now) / 1000));
    const formatted = [Math.floor(seconds / 3600), Math.floor(seconds / 60) % 60, seconds % 60]
        .map(value => String(value).padStart(2, '0')).join(':');
    return <Typography.Text strong role="timer" style={{ fontSize: 28, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        <ClockCircleOutlined aria-hidden="true" style={{ marginRight: 12 }} />{formatted}
    </Typography.Text>;
}
